import { useHistory, useParams } from 'react-router-dom';
import { Badge, Box, Heading, Table, TableContainer, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { BENEFICIARY_DETAILS_ROUTE } from 'constants/routes';
import Pagination from 'modules/approve-transactions-v2/components/Pagination';
import { BENEFICIARY_TABLE_HEADINGS } from 'modules/beneficiaries/constants/constants';
import { formatBeneficiaryStatus, getBeneficiaryStatusBadgeColorScheme } from 'modules/beneficiaries/utils/utils';
import { formatDateTime } from 'utils/utils';
import LoadingErrorWrapper from 'components/LoadingErrorWrapper/LoadingErrorWrapper';
import Search from 'components/SearchBox/Search';

const BeneficiaryList = ({
  title = '',
  beneficiaries = [],
  isLoading = false,
  error = null,
  currentPage = 1,
  totalPages = 0,
  totalCount = 0,
  tab = 0,
  onPageChange,
  onSearch,
}) => {
  const history = useHistory();
  const { superMerchantId } = useParams();

  const handleRowClick = (beneficiary) => {
    history.push(BENEFICIARY_DETAILS_ROUTE(beneficiary?.id), { superMerchantId, tab });
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange?.(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange?.(currentPage + 1);
    }
  };

  return (
    <>
      <Box overflowX='auto' maxWidth='100%' p={4} borderRadius='lg' boxShadow='sm' bg='white'>
        <Heading as='h1' size='md' mb={3} color='gray.700'>
          {title}
          <Badge colorScheme='gray' rounded='full' px={2.5} py={0.5} ml={2}>
            {totalCount}
          </Badge>
        </Heading>

        <Search searchType='Beneficiary' setSearchTerm={(searchTerm) => onSearch?.(searchTerm)} debounceTime={800} />

        <LoadingErrorWrapper isLoading={isLoading} errorTitle='Error fetching beneficiaries' error={error}>
          <TableContainer borderRadius='lg' border='1px solid' borderColor='gray.200' overflowX='auto' mt={4}>
            <Table variant='simple' size='md'>
              <Thead bg='gray.100' position='sticky' top={0} zIndex={1}>
                <Tr>
                  {BENEFICIARY_TABLE_HEADINGS.map((heading, idx) => (
                    <Th key={idx} fontSize='sm' fontWeight='bold' color='gray.700' py={3}>
                      {heading}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              {!!beneficiaries?.length && (
                <Tbody>
                  {beneficiaries?.map((beneficiary) => (
                    <Tr
                      key={beneficiary?.id}
                      fontSize='sm'
                      onClick={() => handleRowClick(beneficiary)}
                      cursor='pointer'
                      _hover={{ bg: 'gray.50' }}
                    >
                      <Td color='gray.600'>{formatDateTime(beneficiary?.created_at)}</Td>
                      <Td color='gray.600'>{beneficiary?.id ?? '-'}</Td>
                      <Td color='gray.600' fontWeight='bold'>
                        {beneficiary?.beneficiary_name || '-'}
                      </Td>
                      <Td color='gray.600'>{beneficiary?.merchant_name || beneficiary?.merchant_id || '-'}</Td>
                      <Td color='gray.600'>{beneficiary?.beneficiary_account_number || '-'}</Td>
                      <Td color='gray.600'>{beneficiary?.beneficiary_bank_name || '-'}</Td>
                      <Td color='gray.600'>{beneficiary?.destination_currency || '-'}</Td>
                      <Td color='gray.600'>{beneficiary?.payout_method || '-'}</Td>
                      <Td>
                        <Badge colorScheme={beneficiary?.has_documents ? 'green' : 'gray'} rounded='full' px={3}>
                          {beneficiary?.has_documents ? 'Yes' : 'No'}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={getBeneficiaryStatusBadgeColorScheme(beneficiary?.status)}
                          rounded='full'
                          px={3}
                        >
                          {formatBeneficiaryStatus(beneficiary?.status)}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              )}
            </Table>
          </TableContainer>
          {!beneficiaries?.length && (
            <Box textAlign='center' mt={4}>
              <Heading size='md' color='gray.500'>
                No Beneficiaries found
              </Heading>
            </Box>
          )}
        </LoadingErrorWrapper>
      </Box>
      {totalPages > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          isNextDisabled={currentPage >= totalPages}
        />
      )}
    </>
  );
};

export default BeneficiaryList;
